import * as yup from 'yup'
import { parse, isDate } from 'date-fns'
import { emailYup, optionalEmailYup } from './emailYup'

export function parseDateString(value, originalValue) {
  if (!isDate(originalValue) && originalValue.length === 0) return null
  if (!isDate(originalValue) && originalValue.length !== 11) return originalValue
  const parsedDate = isDate(originalValue) ? originalValue : parse(originalValue, 'MMM-dd-yyyy', new Date())
  return parsedDate
}

const phoneRegex = /^\(?([0-9]{3})\)? ([0-9]{3})[-]?([0-9]{4})/
const postalCodeRegex = /^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]/
const isBlank = value => !value?.trim()

const requestorSchemaFields = {
  requestor: yup.object().shape(
    {
      isBusiness: yup.bool().required('You must indicate if you are requesting for a business'),
      businessName: yup.string().when('isBusiness', {
        is: true,
        then: () => yup.string().required('Business Name is required')
      }),
      firstName: yup.string().required('First Name is required'),
      middleName: yup.string(),
      lastName: yup.string().required('Last Name is required'),
      phone: yup.string().matches(phoneRegex, { message: 'Phone must be valid', excludeEmptyString: true }),
      email: yup.string().when('address', {
        is: isBlank,
        then: () => emailYup().required('At least one of email and address is required'),
        otherwise: () => optionalEmailYup()
      }),
      confirmEmail: yup.string().test('valid-confirm-email', 'Emails must match', function validateConfirmEmail(value) {
        const { email } = this.parent
        if (!email && !value) return true
        if (!email && value) return this.createError({ message: 'Emails must match' })
        if (email && !value) return this.createError({ message: 'Confirm email is required' })
        if (email !== value) return this.createError({ message: 'Emails must match' })
        return true
      }),
      address: yup
        .string()
        .trim()
        .when('email', {
          is: isBlank,
          then: () => yup.string().trim().required('At least one of email and address is required'),
          otherwise: () => yup.string().trim()
        }),
      unit: yup.string(),
      city: yup.string().when('address', {
        is: address => !isBlank(address),
        then: () => yup.string().required('City is required')
      }),
      province: yup.string().when('address', {
        is: address => !isBlank(address),
        then: () => yup.string().required('Province is required')
      }),
      postalCode: yup
        .string()
        .matches(postalCodeRegex, { message: 'Postal Code must be valid', excludeEmptyString: true })
        .when('address', {
          is: address => !isBlank(address),
          then: () => yup.string().required('Postal Code is required')
        })
    },
    ['email', 'address']
  )
}

const addressSchema = yup.object().shape({
  legalDescription: yup.string(),
  address: yup.string().required('Address is required'),
  unit: yup.string(),
  city: yup.string().required('City is required'),
  province: yup.string(),
  postalCode: yup.string().matches(postalCodeRegex, { message: 'Postal Code must be valid', excludeEmptyString: true }),
  files: yup.array()
})

const requestDetailsSchemaFields = {
  infoType: yup.array().of(yup.string()),
  thirdPartyFiles: yup.array(),
  periodStart: yup.date().nullable().transform(parseDateString).typeError('Date must be valid').max(new Date(), 'Date cannot be in the future'),
  periodEnd: yup
    .date()
    .nullable()
    .transform(parseDateString)
    .typeError('Date must be valid')
    .min(yup.ref('periodStart'), 'Period End Date cannot be before Period Start Date')
    .max(new Date(), 'Date cannot be in the future'),
  requestDescription: yup.string().required('Request description is required'),
  referenceNumbers: yup.string(),
  hasAddresses: yup.bool().nullable(true),
  addresses: yup.array().when('hasAddresses', {
    is: hasAddresses => hasAddresses,
    then: () => yup.array().of(addressSchema),
    otherwise: () => yup.array()
  }),
  accessMethod: yup.string().required('Access method is required')
}

const requestorSchema = yup.object().shape(requestorSchemaFields)
const requestDetailsSchema = yup.object().shape(requestDetailsSchemaFields)
const foiInfoSchema = yup.object().shape({
  ...requestDetailsSchemaFields,
  ...requestorSchemaFields
})

export { requestorSchema, requestDetailsSchema, foiInfoSchema }
